<template>
  <div class="screen-settings-page">
    <div class="page-header">
      <a-button type="link" @click="goBack">
        <template #icon><ArrowLeftOutlined /></template>
        返回
      </a-button>
      <h2 class="page-title">屏幕设置</h2>
    </div>

    <div class="settings-content">
      <!-- 基本信息 -->
      <div class="settings-section">
        <h3 class="section-title">基本信息</h3>
        <a-form :model="formData" :label-col="{ span: 4 }" :wrapper-col="{ span: 16 }">
          <a-form-item label="屏幕名称" required>
            <a-input v-model:value="formData.name" placeholder="请输入屏幕名称" />
          </a-form-item>
          <a-form-item label="屏幕规格" required>
            <a-select v-model:value="formData.spec" placeholder="请选择屏幕规格">
              <a-select-option value="5x4">5x4</a-select-option>
              <a-select-option value="6x4">6x4</a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="备注">
            <a-textarea v-model:value="formData.remarks" placeholder="请输入备注" :rows="3" />
          </a-form-item>
        </a-form>
      </div>

      <!-- 监控车辆列表 -->
      <div class="settings-section">
        <div class="section-header">
          <h3 class="section-title">监控车辆列表</h3>
          <a-button type="primary" @click="openVehicleDialog">
            <template #icon><PlusOutlined /></template>
            添加车辆
          </a-button>
        </div>
        <a-table
          :columns="vehicleColumns"
          :data-source="monitorVehicleList"
          :row-key="(record) => record.id"
          size="middle"
          :pagination="false"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'action'">
              <a style="color: #ff4d4f" @click="removeVehicle(record)">移除</a>
            </template>
          </template>
        </a-table>
        <div v-if="monitorVehicleList.length === 0" class="empty-tip">
          暂无监控车辆，请点击"添加车辆"按钮添加
        </div>
      </div>

      <!-- 底部操作按钮 -->
      <div class="page-footer">
        <a-button @click="goBack">取消</a-button>
        <a-button type="primary" style="margin-left: 16px" @click="handleSave">保存</a-button>
      </div>
    </div>

    <!-- 添加车辆弹框 -->
    <a-modal
      v-model:open="vehicleDialogVisible"
      title="添加监控车辆"
      width="70%"
      :footer="null"
      :bodyStyle="{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }"
    >
      <div class="vehicle-dialog">
        <!-- 筛选区域 -->
        <div class="filter-area">
          <div class="filter-item">
            <span class="filter-label">企业：</span>
            <a-select
              v-model:value="vehicleQuery.deptId"
              placeholder="请选择"
              style="width: 200px"
              allow-clear
            >
              <a-select-option v-for="option in companyList" :key="option.deptId" :value="option.deptId">
                {{ option.name }}
              </a-select-option>
            </a-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">运营公司：</span>
            <a-select
              v-model:value="vehicleQuery.operatorId"
              placeholder="请选择"
              style="width: 200px"
              allow-clear
            >
              <a-select-option v-for="option in operatorList" :key="option.id" :value="option.id">
                {{ option.name }}
              </a-select-option>
            </a-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">牌照/车架号：</span>
            <a-input v-model:value="vehicleQuery.sk" placeholder="请输入" style="width: 200px" />
          </div>
          <a-button type="primary" ghost @click="searchVehicle">查询</a-button>
          <a-button style="margin-left: 8px" @click="resetVehicleQuery">重置</a-button>
        </div>

        <!-- 车辆列表 -->
        <a-table
          :columns="vehicleColumns"
          :data-source="vehicleDataSource"
          :row-key="(record) => record.id"
          :row-selection="rowSelection"
          size="middle"
          :pagination="vehiclePagination"
          @change="handleVehicleTableChange"
        >
        </a-table>

        <!-- 弹框底部按钮 -->
        <div class="dialog-footer">
          <a-button @click="vehicleDialogVisible = false">取消</a-button>
          <a-button type="primary" style="margin-left: 16px" @click="confirmAddVehicle">确定</a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons-vue';
import { Modal, message } from 'ant-design-vue';
import { getVehiclePageList } from '@/api/vehicle/index';
import { getCompanyList } from '@/api/enterprise/index';
import { getOperatorList } from '@/api/settings/operator';
import { getScreenInfo, updateScreen } from '@/api/monitor/screen';

const router = useRouter();
const route = useRoute();

// 基本信息
const formData = reactive({
  name: '',
  spec: undefined,
  remarks: ''
});

// 监控车辆列表
const monitorVehicleList = ref([]);

// 车辆表格列配置
const vehicleColumns = [
  {
    title: '企业名称',
    dataIndex: 'deptName',
    key: 'deptName',
    width: 220
  },
  {
    title: '车辆编码',
    dataIndex: 'licenceNo',
    key: 'licenceNo',
    width: 120
  },
  {
    title: '车辆识别代码',
    dataIndex: 'vin',
    key: 'vin',
    width: 180
  },
  {
    title: '车辆类型',
    dataIndex: 'vehicleTypeName',
    key: 'vehicleTypeName',
    width: 120
  },
  {
    title: '运营公司',
    dataIndex: 'operatorName',
    key: 'operatorName',
    width: 150
  },
  {
    title: '操作',
    key: 'action',
    width: 80,
    align: 'center'
  }
];

// 车辆选择弹框
const vehicleDialogVisible = ref(false);
const companyList = ref([]);
const operatorList = ref([]);

// 车辆筛选条件
const vehicleQuery = reactive({
  deptId: undefined,
  operatorId: undefined,
  sk: ''
});

// 车辆列表数据
const vehicleDataSource = ref([]);
const selectedVehicleKeys = ref([]);

// 车辆分页
const vehiclePagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`
});

// 行选择配置
const rowSelection = computed(() => ({
  selectedRowKeys: selectedVehicleKeys.value,
  onChange: (keys) => {
    selectedVehicleKeys.value = keys;
  },
  getCheckboxProps: (record) => ({
    disabled: monitorVehicleList.value.some(item => item.id === record.id)
  })
}));

// 返回
const goBack = () => {
  router.back();
};

// 打开车辆选择弹框
const openVehicleDialog = () => {
  vehicleDialogVisible.value = true;
  selectedVehicleKeys.value = [];
  loadVehicleData();
};

// 加载车辆数据
const loadVehicleData = async () => {
  try {
    const data = {
      pageIndex: vehiclePagination.current,
      pageSize: vehiclePagination.pageSize,
      conditionMap: { ...vehicleQuery }
    };
    const res = await getVehiclePageList(data);
    if (res.code === 200) {
      vehicleDataSource.value = res.data.records || [];
      vehiclePagination.total = res.data.total;
    }
  } catch (error) {
    console.error('获取车辆列表失败:', error);
  }
};

// 搜索车辆
const searchVehicle = () => {
  vehiclePagination.current = 1;
  loadVehicleData();
};

// 重置车辆筛选
const resetVehicleQuery = () => {
  vehicleQuery.deptId = undefined;
  vehicleQuery.operatorId = undefined;
  vehicleQuery.sk = '';
  vehiclePagination.current = 1;
  loadVehicleData();
};

// 车辆表格变化
const handleVehicleTableChange = (pagination) => {
  vehiclePagination.current = pagination.current;
  vehiclePagination.pageSize = pagination.pageSize;
  loadVehicleData();
};

// 确认添加车辆
const confirmAddVehicle = () => {
  if (selectedVehicleKeys.value.length === 0) {
    message.warning('请选择要添加的车辆');
    return;
  }

  const newVehicles = vehicleDataSource.value.filter(
    item => selectedVehicleKeys.value.includes(item.id) && 
            !monitorVehicleList.value.some(v => v.id === item.id)
  );

  monitorVehicleList.value.push(...newVehicles);
  vehicleDialogVisible.value = false;
  message.success('添加成功');
};

// 移除车辆
const removeVehicle = (record) => {
  Modal.confirm({
    title: '确认移除',
    content: `确定要移除车辆"${record.licenceNo || record.vin}"吗？`,
    okText: '确认',
    cancelText: '取消',
    onOk: () => {
      const index = monitorVehicleList.value.findIndex(item => item.id === record.id);
      if (index > -1) {
        monitorVehicleList.value.splice(index, 1);
        message.success('移除成功');
      }
    }
  });
};

// 保存设置
const handleSave = () => {
  if (!formData.name) {
    message.warning('请输入屏幕名称');
    return;
  }
  if (!formData.spec) {
    message.warning('请选择屏幕规格');
    return;
  }

  const data = {
    id: route.query.id,
    ...formData,
    vehicleList: monitorVehicleList.value
  };
  updateScreen(data).then((res) => {
    if (res.code === 200) {
      message.success('保存成功');
      router.back();
    }
  });
};

onMounted(async () => {
  // 获取屏幕ID
  const screenId = route.query.id;

  // 获取企业列表
  try {
    const companyRes = await getCompanyList();
    if (companyRes.code === 200) {
      companyList.value = companyRes.data || [];
    }
  } catch (error) {
    console.error('获取企业列表失败:', error);
  }

  // 获取运营公司列表
  try {
    const operatorRes = await getOperatorList();
    if (operatorRes.code === 200) {
      operatorList.value = operatorRes.data || [];
    }
  } catch (error) {
    console.error('获取运营公司列表失败:', error);
  }

  // 获取屏幕设置数据
  if (screenId) {
    try {
      const res = await getScreenInfo(screenId);
      if (res.code === 200 && res.data) {
        formData.name = res.data.name || '';
        formData.spec = res.data.spec || undefined;
        formData.remarks = res.data.remarks || '';
        monitorVehicleList.value = res.data.vehicleList || [];
      }
    } catch (error) {
      console.error('获取屏幕信息失败:', error);
    }
  }
});
</script>

<style lang="less" scoped>
.screen-settings-page {
  padding: 20px;
  background-color: #ffffff;
  min-height: calc(100vh - 70px - 57px);

  .page-header {
    display: flex;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      margin: 0 0 0 16px;
      font-size: 18px;
      font-weight: 500;
      color: #0A212E;
    }
  }

  .settings-content {
    max-width: 1200px;
    margin: 0 auto;

    .settings-section {
      margin-bottom: 32px;
      padding: 24px;
      background: #fafafa;
      border-radius: 8px;

      .section-title {
        margin: 0 0 20px 0;
        font-size: 16px;
        font-weight: 500;
        color: #0A212E;
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        .section-title {
          margin: 0;
        }
      }

      .empty-tip {
        text-align: center;
        padding: 40px 0;
        color: #999;
        font-size: 14px;
      }
    }

    .page-footer {
      margin-top: 32px;
      padding-top: 16px;
      border-top: 1px solid #e8e8e8;
      text-align: center;
    }
  }
}

.vehicle-dialog {
  .filter-area {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
    padding: 16px;
    background: #fafafa;
    border-radius: 4px;

    .filter-item {
      display: flex;
      align-items: center;

      .filter-label {
        flex-shrink: 0;
        margin-right: 8px;
        color: #68767E;
      }
    }
  }

  .dialog-footer {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e8e8e8;
    text-align: right;
  }
}

a {
  color: #1E8EF2;
  cursor: pointer;
}
</style>